/**
 * Главная страница раздела «Охрана труда» (`/ot`, p2-ot-section §9.2): единый список сотрудники +
 * вакантные рабочие места, группировка по подразделениям, 7 фильтров-чипов, поиск по ФИО/
 * подразделению/должности. БЕЗ кнопок «Заказать…» (design D4): клик по строке ведёт строго в
 * карточку сотрудника (`/staff/:id`) или рабочего места (`/workplaces/:id`).
 *
 * F2-10 (реверс дизайн-решения D3, санкционирован заказчиком): вместо ОДНОЙ колонки «Статус»
 * (цветной бейдж overallStatus) — 5 колонок «Сотрудник/Должность/СОУТ/Профриски/Обучение» с
 * СОДЕРЖИМЫМ (класс+срок/уровень риска/список сроков по программам), не только цветом. Сменщики
 * (design «Сменщики» §10.1) вложены под основным сотрудником: имена — под ФИО, «сменный режим» +
 * счётчик — под должностью; строки сменщиков-как-сотрудников (если они САМИ занимают другое
 * место) остаются обычными строками таблицы, БЕЗ дублирования вложенности.
 *
 * F2-17: кнопка «Добавить в структуру» в шапке открывает модалку (импорт из Excel / единая форма
 * «Добавление рабочего места» — см. `AddToStructureModal`, §7 p3-structure-sout-training волна 7).
 * После успеха любой вкладки — модалка закрывается и таблица перезагружается повторным вызовом
 * `companyApi.otOverview()`.
 *
 * Замечание РП №4 (p2-remarks-2, волна C), актуально и для единой формы §7: эта же таблица
 * показывает ТОЛЬКО занятые места ∪ `hasNoEmployee=true` — место без сотрудника с
 * `hasNoEmployee=false` было бы НАВСЕГДА невидимо здесь (хотя сохранялось и было видно в
 * «Сотрудники → Структура»). `AddWorkplaceForm` автоматически форсирует `hasNoEmployee=true`, когда
 * сотрудник не выбран, а `handleAddSuccess` показывает inline `Alert` с уведомлением, привязанным
 * к созданному месту, — и предупреждает, что оно может быть скрыто активным фильтром/поиском.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { HardHat, Plus } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { companyApi, type OtOverviewRow, type OtTrainingItem, type Workplace } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { backState } from '@/lib/use-back-target'
import { OT_STATUS_VIEW, soutContentLabel } from '@/lib/ot'
import { RISK_LEVEL_VIEW } from '@/lib/profrisk'
import { TRAINING_STATUS_VIEW } from '@/lib/training'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Input } from '@/design-system/components/primitives/input'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { AddToStructureModal, type AddToStructureSuccessInfo } from '@/company/add-to-structure-modal'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableGroupRow,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import { cn } from '@/lib/utils'

const OT_TABLE_COLUMN_COUNT = 5

/**
 * 7 фильтров §9.2 — намеренно РАЗЛИЧАЮТ подсистему-источник проблемы (не просто «всё красное»):
 * training-overdue/sout-overdue/risk-high все схлопываются в overallStatus='overdue', поэтому
 * фильтр смотрит на СВОЙ сигнал строки (`trainingSignal`/`soutSignal`/`profriskSignal`), а не на
 * агрегированный `overallStatus` — иначе три разных фильтра давали бы один и тот же результат.
 */
type OtFilterKey =
  | 'training_overdue'
  | 'sout_overdue'
  | 'risk_high'
  | 'risk_medium'
  | 'risk_acceptable'
  | 'no_data'
  | 'all_ok'

const FILTERS: { key: OtFilterKey; label: string; match: (row: OtOverviewRow) => boolean }[] = [
  { key: 'training_overdue', label: 'Просроченное обучение', match: (r) => r.trainingSignal === 'overdue' },
  { key: 'sout_overdue', label: 'Просроченная СОУТ', match: (r) => r.soutSignal === 'overdue' },
  { key: 'risk_high', label: 'Высокий риск', match: (r) => r.profriskSignal === 'overdue' },
  { key: 'risk_medium', label: 'Средний риск', match: (r) => r.profriskSignal === 'expiring' },
  { key: 'risk_acceptable', label: 'Допустимый риск', match: (r) => r.profriskSignal === 'ok' },
  { key: 'no_data', label: 'Нет данных', match: (r) => r.overallStatus === 'no_data' },
  { key: 'all_ok', label: 'Всё ок', match: (r) => r.overallStatus === 'ok' },
]

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'whitespace-nowrap rounded-pill border px-3 py-1.5 text-13 font-medium transition-colors',
        active
          ? 'border-accent bg-accent-bg text-accent-txt'
          : 'border-line bg-white text-txt-secondary hover:bg-hover',
      )}
    >
      {label}
    </button>
  )
}

function rowSearchText(row: OtOverviewRow): string {
  return `${row.fullName ?? ''} ${row.departmentName} ${row.position}`.toLowerCase()
}

/** Заголовок «Нет данных» — единый нейтральный ярлык для пустых колонок содержимого (F2-10). */
function NoDataLabel() {
  return <span className="text-txt-tertiary">Нет данных</span>
}

/** Колонка «СОУТ»: класс+срок бейджем (цвет — авторитетный soutSignal с сервера), либо «Нет данных». */
function SoutCell({ row }: { row: OtOverviewRow }) {
  const label = soutContentLabel(row.sout)
  if (!label) return <NoDataLabel />
  const role = OT_STATUS_VIEW[row.soutSignal].role
  return <Badge status={role}>{label}</Badge>
}

/** Колонка «Профриски»: уровень риска бейджем (цвет по палитре уровня риска), либо «Нет данных». */
function ProfriskCell({ row }: { row: OtOverviewRow }) {
  if (!row.profrisk) return <NoDataLabel />
  const view = RISK_LEVEL_VIEW[row.profrisk.riskLevel]
  return <Badge status={view.role}>{view.label}</Badge>
}

/**
 * Колонка «Обучение»: стопка бейджей — по одному на программу (F2-10, не единая клетка).
 * У вакансии обучение не применимо вовсе — «—», в отличие от «Нет данных» у сотрудника без записей.
 */
function TrainingCell({ row }: { row: OtOverviewRow }) {
  if (row.kind === 'vacancy') return <span className="text-txt-tertiary">—</span>
  if (row.training.length === 0) return <NoDataLabel />
  return (
    <div className="flex flex-col gap-1">
      {row.training.map((item: OtTrainingItem, idx) => (
        <Badge
          key={`${item.programName}-${idx}`}
          status={TRAINING_STATUS_VIEW[item.status].role}
          title={item.programName}
          className="w-fit"
        >
          {item.dueLabel}
        </Badge>
      ))}
    </div>
  )
}

/** Вложенные сменщики под ФИО основного сотрудника (design «Сменщики» §10.1, screenshot F2-10). */
function EmployeeCell({ row }: { row: OtOverviewRow }) {
  if (row.kind === 'vacancy') {
    return <Badge variant="gray">рабочее место без сотрудника</Badge>
  }
  return (
    <div>
      <div className="font-medium text-txt">{row.fullName}</div>
      {row.isShift && row.shiftPartners && row.shiftPartners.length > 0 ? (
        <div className="mt-1 flex flex-col gap-0.5">
          {row.shiftPartners.map((p) => (
            <div key={p.id} className="text-13 text-txt-secondary">
              {p.fullName} · сменщик
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** Заметка «сменный режим» + счётчик под должностью основного сотрудника. */
function PositionCell({ row }: { row: OtOverviewRow }) {
  const shiftCount = row.isShift && row.shiftPartners ? row.shiftPartners.length + 1 : 0
  return (
    <div>
      <div>{row.position}</div>
      {shiftCount > 1 ? (
        <div className="mt-1 flex flex-col gap-0.5 text-13 text-txt-tertiary">
          <span>сменный режим</span>
          <span>{shiftCount} человека</span>
        </div>
      ) : null}
    </div>
  )
}

/** Группа-фрагмент (заголовок подразделения + строки) — по образцу employees-manager.tsx. */
function OtGroupRows({ dept, rows, onOpen }: { dept: string; rows: OtOverviewRow[]; onOpen: (r: OtOverviewRow) => void }) {
  return (
    <>
      <DataTableGroupRow colSpan={OT_TABLE_COLUMN_COUNT}>{dept}</DataTableGroupRow>
      {rows.map((r) => (
        <DataTableRow key={r.employeeId ?? r.workplaceId} clickable onClick={() => onOpen(r)}>
          <DataTableCell>
            <EmployeeCell row={r} />
          </DataTableCell>
          <DataTableCell>
            <PositionCell row={r} />
          </DataTableCell>
          <DataTableCell>
            <SoutCell row={r} />
          </DataTableCell>
          <DataTableCell>
            <ProfriskCell row={r} />
          </DataTableCell>
          <DataTableCell>
            <TrainingCell row={r} />
          </DataTableCell>
        </DataTableRow>
      ))}
    </>
  )
}

export function OtOverviewPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [rows, setRows] = useState<OtOverviewRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<OtFilterKey | null>(null)
  const [addModalOpen, setAddModalOpen] = useState(false)
  // Замечание РП №4: успех вкладки «Рабочее место без сотрудника» — уведомление привязано к
  // конкретному созданному месту (`workplace`); `mayBeHidden` снимается ОДИН раз, В МОМЕНТ
  // создания (пока фильтр/поиск ещё те, что видел пользователь) — не пересчитывается при
  // последующих правках фильтра/поиска. Уведомление показывается ТОЛЬКО для этой вкладки —
  // импорт/сотрудник свой результат не подсвечивают (см. `AddToStructureSuccessInfo`).
  const [workplaceNotice, setWorkplaceNotice] = useState<{
    workplace: Workplace
    mayBeHidden: boolean
  } | null>(null)
  // Request-sequencing (F2-17 фикс гонки reload): держим контроллер ТЕКУЩЕГО запроса и на каждый
  // loadRows делаем abort-then-replace. Любой commit (setRows/setError/setLoading) гейтится
  // `controller.signal.aborted` — отменённый (более новым reload или размонтированием) запрос НИЧЕГО
  // не пишет. Это «last REQUEST wins», а не «last RESPONSE wins»: без этого устаревший снимок,
  // резолвнувшийся ПОСЛЕ свежего, затирал бы только что добавленные через модалку строки.
  const loadAbortRef = useRef<AbortController | null>(null)

  const loadRows = useCallback(() => {
    loadAbortRef.current?.abort()
    const controller = new AbortController()
    loadAbortRef.current = controller
    setLoading(true)
    setError(null)
    companyApi
      .otOverview(controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return
        setRows(res.items)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(getErrorMessage(err, 'Не удалось загрузить раздел «Охрана труда»'))
      })
      .finally(() => {
        if (controller.signal.aborted) return
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    loadRows()
    return () => loadAbortRef.current?.abort()
  }, [loadRows])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const filterDef = activeFilter ? FILTERS.find((f) => f.key === activeFilter) : null
    return rows.filter((r) => {
      if (filterDef && !filterDef.match(r)) return false
      if (q && !rowSearchText(r).includes(q)) return false
      return true
    })
  }, [rows, search, activeFilter])

  const grouped = useMemo(() => {
    const map = new Map<string, OtOverviewRow[]>()
    for (const r of filtered) {
      const arr = map.get(r.departmentName) ?? []
      arr.push(r)
      map.set(r.departmentName, arr)
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [filtered])

  function openRow(row: OtOverviewRow) {
    // D1, p3-structure-sout-training §2: передаём адрес этой страницы как state.from — «Назад»
    // в открытой карточке должен вернуть на «Охрана труда», а не всегда на «Сотрудники».
    const state = backState(location)
    if (row.kind === 'employee' && row.employeeId) {
      navigate(`/staff/${row.employeeId}`, { state })
    } else if (row.workplaceId) {
      navigate(`/workplaces/${row.workplaceId}`, { state })
    }
  }

  /**
   * Замечание РП №4: перезагружаем таблицу при успехе ЛЮБОЙ вкладки (как раньше), но уведомление
   * показываем только для «Рабочее место без сотрудника» — у неё, в отличие от импорта/сотрудника,
   * созданная запись раньше НИЧЕМ не подсвечивалась и терялась из виду при активном фильтре/поиске.
   * `activeFilter`/`search` читаем ЗДЕСЬ, в момент создания — фильтры не сбрасываем.
   */
  function handleAddSuccess(info: AddToStructureSuccessInfo) {
    loadRows()
    if (info.tab === 'workplace') {
      setWorkplaceNotice({
        workplace: info.workplace,
        mayBeHidden: activeFilter !== null || search.trim() !== '',
      })
    } else {
      setWorkplaceNotice(null)
    }
  }

  return (
    <div>
      <PageHead
        title="Охрана труда"
        subtitle="Сотрудники и рабочие места вашей организации — обучение, СОУТ, профриски одним взглядом"
        actions={
          <Button
            leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
            onClick={() => setAddModalOpen(true)}
          >
            Добавить в структуру
          </Button>
        }
      />

      {addModalOpen ? (
        <AddToStructureModal
          api={companyApi}
          onClose={() => setAddModalOpen(false)}
          onSuccess={handleAddSuccess}
        />
      ) : null}

      {/* Полноэкранный плейсхолдер/ошибка — только для ПЕРВОЙ загрузки (когда показывать ещё нечего).
          При user-triggered reload (после успеха модалки) строки уже есть → тулбар фильтров/поиска
          и таблица остаются смонтированы (F2-17 фикс флика), ошибка reload — алертом над тулбаром. */}
      {loading && rows.length === 0 ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : error && rows.length === 0 ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          {error ? (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          ) : null}
          {workplaceNotice ? (
            <Alert
              variant="info"
              title={`Рабочее место «${workplaceNotice.workplace.position}» добавлено в «${workplaceNotice.workplace.departmentName}»`}
              className="mb-4"
              actions={
                <Button variant="secondary" size="sm" onClick={() => setWorkplaceNotice(null)}>
                  Скрыть
                </Button>
              }
            >
              {workplaceNotice.mayBeHidden
                ? 'Место может быть не видно в списке из-за активного фильтра или поиска — сами фильтры менять не стали.'
                : null}
            </Alert>
          ) : null}
          <Toolbar aria-label="Фильтры раздела «Охрана труда»">
            {FILTERS.map((f) => (
              <FilterChip
                key={f.key}
                label={f.label}
                active={activeFilter === f.key}
                onClick={() => setActiveFilter((cur) => (cur === f.key ? null : f.key))}
              />
            ))}
          </Toolbar>

          <Toolbar aria-label="Поиск">
            <div className="max-w-narrow flex-1">
              <Input
                placeholder="Поиск по ФИО, подразделению или должности"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Поиск по ФИО, подразделению или должности"
              />
            </div>
          </Toolbar>

          {grouped.length === 0 ? (
            <Panel>
              <EmptyState
                icon={HardHat}
                title="Ничего не найдено"
                description="Измените фильтр или поисковый запрос"
              />
            </Panel>
          ) : (
            <Panel>
              <DataTable>
                <DataTableHead>
                  <DataTableRow>
                    <DataTableHeaderCell>Сотрудник</DataTableHeaderCell>
                    <DataTableHeaderCell>Должность</DataTableHeaderCell>
                    <DataTableHeaderCell>СОУТ</DataTableHeaderCell>
                    <DataTableHeaderCell>Профриски</DataTableHeaderCell>
                    <DataTableHeaderCell>Обучение</DataTableHeaderCell>
                  </DataTableRow>
                </DataTableHead>
                <DataTableBody>
                  {grouped.map(([dept, groupRows]) => (
                    <OtGroupRows key={dept} dept={dept} rows={groupRows} onOpen={openRow} />
                  ))}
                </DataTableBody>
              </DataTable>
            </Panel>
          )}
        </>
      )}
    </div>
  )
}
